const ADDED_STATE_DURATION = 2200;
const ADDED_STATE_ICON =
  '<svg class="btn__added__icon" xmlns="http://www.w3.org/2000/svg" width="15" height="11" viewBox="0 0 15 11" fill="none" aria-hidden="true" focusable="false">' +
  '<path fill-rule="evenodd" clip-rule="evenodd" d="M14.7071 0.292893C15.0976 0.683417 15.0976 1.31658 14.7071 1.70711L5.70711 10.7071C5.31658 11.0976 4.68342 11.0976 4.29289 10.7071L0.292893 6.70711C-0.0976311 6.31658 -0.0976311 5.68342 0.292893 5.29289C0.683417 4.90237 1.31658 4.90237 1.70711 5.29289L5 8.58579L13.2929 0.292893C13.6834 -0.0976311 14.3166 -0.0976311 14.7071 0.292893Z"/>' +
  '</svg>';

// Flips a button to "item added" and back. Used for adds that do not open the
// cart drawer, so the shopper still gets a clear answer on what they pressed.
function showAddedToCartState(button){
  if(!button) return;

  let added = button.querySelector('.btn__added');
  if(!added){
    added = document.createElement('span');
    added.className = 'btn__added';
    added.setAttribute('aria-hidden', 'true');
    added.innerHTML = `${ADDED_STATE_ICON}<span>${window.cartStrings?.itemAdded || 'Item added to cart'}</span>`;
    button.appendChild(added);
    button.classList.add('btn--has-added-state');
  }

  clearTimeout(button.addedStateTimeout);
  button.classList.remove('btn--added');
  void button.offsetWidth; // replay the tick animation on repeat adds
  button.classList.add('btn--added');
  button.addedStateTimeout = setTimeout(() => button.classList.remove('btn--added'), ADDED_STATE_DURATION);
}

// The drawer decides this from the same is-empty flag - see CartDrawer.shouldAutoOpen.
function cartDrawerWillOpen(cart, cartWasEmpty){
  if(!cart) return false;
  if(typeof cart.shouldAutoOpen !== 'function') return true;
  return cart.opened || cart.shouldAutoOpen(cartWasEmpty);
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.form && this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            // Read before renderContents mutates the drawer, so both see the
            // same cart state.
            const cartWasEmpty = this.cart.classList.contains('is-empty');
            const quietAdd = !cartDrawerWillOpen(this.cart, cartWasEmpty);
            this.cart.renderContents(response);
            if (quietAdd) showAddedToCartState(this.submitButton);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}

class stickyCartModal extends HTMLElement {
  constructor() {
    super();
    this.boundUpdate = this.update.bind(this);
    window.addEventListener('scroll', this.boundUpdate, { passive: true });
    window.addEventListener('resize', this.boundUpdate);
    window.addEventListener('load', this.boundUpdate);
    this.update();
    this.observeAddButton();
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.querySelector('.sticky-cart-button').addEventListener('click', this.onClickHandler.bind(this));
    this.querySelector('.sticky-cart-button').addEventListener('keydown', this.onClickKeydownHandler.bind(this));
  }
  disconnectedCallback(){
    window.removeEventListener('scroll', this.boundUpdate);
    window.removeEventListener('resize', this.boundUpdate);
    window.removeEventListener('load', this.boundUpdate);
    this.observer?.disconnect();
  }
  // Re-evaluate on layout shifts (lazy images, variant changes) so the bar is
  // correct on the first fold, not only after a scroll event.
  observeAddButton(){
    const addButton = this.getAddButton();
    if(!addButton || !('IntersectionObserver' in window)) return;
    this.observer = this.observer || new IntersectionObserver(this.boundUpdate, { threshold: [0, 1] });
    if(this.observedButton === addButton) return;
    if(this.observedButton) this.observer.unobserve(this.observedButton);
    this.observedButton = addButton;
    this.observer.observe(addButton);
  }
  getAddButton(){
    const sectionId = this.dataset.section;
    const buttonContainer = document.getElementById(`product-form-${sectionId}`);
    const sect = this.closest('.shopify-section, .popup-modal__content__data');
    const hasSelectOption = sect?.querySelector('select-option-js');
    const customAddButton = buttonContainer?.querySelector('.select-options-button');
    return (hasSelectOption && customAddButton) ? customAddButton : buttonContainer?.querySelector('[name="add"]');
  }
  update(){
    const addButton = this.getAddButton();
    if(!addButton) return false;
    // The theme swaps the "select options" button for the real one once a
    // variant is picked, so follow whichever button is live.
    if(this.observedButton !== addButton) this.observeAddButton();

    const rect = addButton.getBoundingClientRect();
    // Not rendered at all (hidden section / display:none) - leave the bar alone.
    if(rect.width === 0 && rect.height === 0) return false;

    // The main button counts as visible only while it is inside the viewport,
    // so the sticky bar shows on the first fold too when the button sits below it.
    const addButtonInView = rect.bottom > 0 && rect.top < window.innerHeight;

    const footer = document.querySelector('.footer__content');
    const footerInView = footer && footer.getBoundingClientRect().top < window.innerHeight;
    const showModal = !addButtonInView && !footerInView;

    if(showModal && !this.classList.contains('show-modal')){
      this.classList.add('show-modal');
      document.querySelector('body').classList.add('modal__sticky-cart');
      if(footer) footer.classList.add('sticky-cart-modal_bottom_padding');
    }
    if(!showModal && this.classList.contains('show-modal')){
      this.classList.remove('show-modal');
      document.querySelector('body').classList.remove('modal__sticky-cart');
      if(footerInView && footer) footer.classList.remove('sticky-cart-modal_bottom_padding');
    }
  }
  onClickKeydownHandler(event){
    if(event.code.toUpperCase() !== 'ENTER') return false;
    this.onClickHandler();
  }
  // A variant still has to be picked while the theme keeps its "select options"
  // markers alive - they are removed as soon as a variant is resolved.
  needsVariantSelection(){
    if(this.dataset.hasVariants !== 'true') return false;
    const sect = this.closest('.shopify-section, .popup-modal__content__data');
    return !!(sect?.querySelector('select-option-js') || this.querySelector('.sticky-cart__select_options__text'));
  }
  getPopup(){
    const modal = document.getElementById(`StickyCartPopup-${this.dataset.section}`);
    const popup = modal?.querySelector('sticky-cart-popup');
    return (modal && popup) ? { modal, popup } : null;
  }
  onClickHandler(){
    if(!this.needsVariantSelection()){
      this.addToCart();
      return;
    }
    const popup = this.getPopup();
    if(popup){
      popup.popup.syncFromProductPage();
      popup.modal.show(this.querySelector('.sticky-cart-button'));
      return;
    }
    var item = document.querySelector(`.product__column__content [id*="variant-selects-"]`),
        btn_item = document.getElementById("product-container"),
        _y = (item ? this.getTop(item) : this.getTop(btn_item)) - 20;
    this.scrollTo(_y,700);
  }
  // The rendered data-variant-id is never refreshed, so read the variant the
  // product form currently holds.
  getCurrentVariantId(){
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    return productForm?.querySelector('input[name="id"]')?.value || this.dataset.variantId;
  }
  addToCart({ variantId, button, onSuccess, onComplete } = {}){
    button = button || this.querySelector('.sticky-cart-button');
    if(button.classList.contains('loading')) return;
    button.classList.add('loading');
    button.setAttribute('aria-disabled', true);
    button.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    const body = {
      id: variantId || this.getCurrentVariantId(),
      quantity: 1
    };
    if(this.cart){
      body.sections = this.cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      this.cart.setActiveElement(document.activeElement);
    }
    config.body = JSON.stringify(body);

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if(response.status){
          console.error(response.description || response.message);
          return;
        }
        if(!this.cart){
          window.location = window.routes.cart_url;
          return;
        }
        if(onSuccess) onSuccess();
        const cartWasEmpty = this.cart.classList.contains('is-empty');
        const quietAdd = !cartDrawerWillOpen(this.cart, cartWasEmpty);
        this.cart.renderContents(response);
        // The sheet closes on success, so its own button would never be seen -
        // confirm on the sticky bar the shopper is left looking at.
        if(quietAdd) showAddedToCartState(this.querySelector('.sticky-cart-button'));
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
        button.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        if(this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if(onComplete) onComplete();
      });
  }
  getTop(el) {
    return el.offsetTop + (el.offsetParent && this.getTop(el.offsetParent));
  }
  scrollTo(to, duration) {
    const element = document.scrollingElement || document.documentElement,
          start = element.scrollTop,
          change = to - start,
    animateScroll = function() {
      element.scrollTop = to;
    };
    animateScroll();
  }
}
customElements.define('sticky-cart-modal', stickyCartModal);

class StickyCartPopup extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.section;
    this.modal = this.closest('.popup-modal');
    this.submitButton = this.querySelector('.sticky-cart-popup__submit');
    this.priceTarget = this.querySelector('[data-sticky-popup-price]');
    this.globalError = this.querySelector('[data-popup-error]');
    this.optionGroups = Array.from(this.querySelectorAll('.sticky-cart-popup__option'));
    this.variants = this.getVariants();

    this.addEventListener('change', this.onOptionChange.bind(this));
    this.submitButton?.addEventListener('click', this.onSubmit.bind(this));
    this.querySelector('.sticky-cart-popup__handle')?.addEventListener('click', () => this.modal?.close());
    document.body.addEventListener('modalClosed', this.onModalClosed.bind(this));
    this.querySelectorAll('.sticky-cart-popup__size-chart').forEach((opener) => {
      opener.addEventListener('click', this.onSizeChartOpen.bind(this));
    });
    this.initDrag();
  }
  get stickyCart(){
    return document.getElementById(`sticky-cart-${this.sectionId}`);
  }
  get variantSelects(){
    return document.getElementById(`variant-selects-${this.sectionId}`);
  }
  getVariants(){
    try {
      return JSON.parse(this.querySelector('.sticky-cart-popup__data').textContent);
    } catch (e) {
      console.error(e);
      return [];
    }
  }
  onOptionChange(event){
    const input = event.target.closest('input[type="radio"]');
    if(!input) return;
    const group = input.closest('.sticky-cart-popup__option');
    if(!group) return;
    group.querySelector('[data-selected-value]').textContent = input.value;
    group.querySelector('[data-option-error]').hidden = true;
    if(this.globalError) this.globalError.hidden = true;
    this.updateSelection();
    // Syncing the page picker re-renders the whole product section, so it is
    // deferred until the sheet is done - see syncToProductPage.
    this.syncPending = true;
  }
  // Option groups are rendered in option order, so their values line up with
  // the variant options array.
  updateSelection(){
    const selected = this.optionGroups.map((group) => group.querySelector('input:checked')?.value || null);
    this.currentVariant = selected.includes(null)
      ? null
      : this.variants.find((variant) => variant.options.every((value, index) => value === selected[index]));
    if(this.currentVariant && this.priceTarget) this.priceTarget.innerHTML = this.currentVariant.price;
    return selected;
  }
  // Drive the product page picker so the page, the URL and the sticky bar state
  // all follow the choice made in the sheet.
  //
  // One change event makes the theme refetch and re-render the entire product
  // section, refresh pickup availability and rebuild every swiper, so every
  // option is set quietly first and a single event is fired at the end - and
  // never while the shopper is waiting on the cart.
  syncToProductPage(){
    this.syncPending = false;
    const variantSelects = this.variantSelects;
    if(!variantSelects) return;

    let lastChanged = null;
    this.optionGroups.forEach((group) => {
      const value = group.querySelector('input:checked')?.value;
      if(!value) return;
      const optionName = group.dataset.optionName;

      const select = Array.from(variantSelects.querySelectorAll('select')).find(
        (item) => item.name === `options[${optionName}]`
      );
      if(select){
        if(select.value === value) return;
        if(!Array.from(select.options).some((option) => option.value === value)) return;
        select.value = value;
        lastChanged = select;
        return;
      }

      const radio = Array.from(variantSelects.querySelectorAll('input[type="radio"]')).find(
        (item) => item.name === optionName && item.value === value
      );
      if(!radio || radio.checked) return;
      radio.checked = true;
      lastChanged = radio;
    });

    lastChanged?.dispatchEvent(new Event('change', { bubbles: true }));
  }
  onModalClosed(){
    if(!this.syncPending || this.modal?.classList.contains('animate')) return;
    if(this.addInFlight){
      this.syncAfterAdd = true;
      return;
    }
    this.syncToProductPage();
  }
  // Carry over anything already chosen on the page before the sheet is shown.
  syncFromProductPage(){
    const variantSelects = this.variantSelects;
    if(!variantSelects) return;

    this.optionGroups.forEach((group) => {
      const optionName = group.dataset.optionName;
      const select = Array.from(variantSelects.querySelectorAll('select')).find(
        (item) => item.name === `options[${optionName}]`
      );
      const value = select
        ? select.value
        : Array.from(variantSelects.querySelectorAll('input[type="radio"]')).find(
            (item) => item.name === optionName && item.checked
          )?.value;
      if(!value) return;

      const input = Array.from(group.querySelectorAll('input[type="radio"]')).find(
        (item) => item.value === value && !item.disabled
      );
      if(!input) return;
      input.checked = true;
      group.querySelector('[data-selected-value]').textContent = value;
      group.querySelector('[data-option-error]').hidden = true;
    });
    this.updateSelection();
  }
  onSubmit(){
    const selected = this.updateSelection();
    let missing = false;
    this.optionGroups.forEach((group, index) => {
      const chosen = selected[index] !== null;
      group.querySelector('[data-option-error]').hidden = chosen;
      if(!chosen) missing = true;
    });
    if(missing) return;

    if(!this.currentVariant || !this.currentVariant.available){
      if(this.globalError) this.globalError.hidden = false;
      return;
    }
    if(this.globalError) this.globalError.hidden = true;

    const stickyCart = this.stickyCart;
    if(!stickyCart) return;
    this.addInFlight = true;
    stickyCart.addToCart({
      variantId: this.currentVariant.id,
      button: this.submitButton,
      onSuccess: this.closeForCart.bind(this),
      onComplete: this.onAddComplete.bind(this)
    });
  }
  // The page can catch up once the cart has answered and the drawer is up -
  // doing it any earlier just competes with the request the shopper is waiting on.
  onAddComplete(){
    this.addInFlight = false;
    // Only when the sheet actually closed - a failed add leaves it open, and
    // the sync then waits for the shopper to dismiss it.
    if(!this.syncAfterAdd) return;
    this.syncAfterAdd = false;
    setTimeout(() => this.syncToProductPage(), 800);
  }
  // The cart drawer keeps the scroll lock only while a popup is still active,
  // so restore it once this sheet has finished closing underneath it.
  closeForCart(){
    this.modal?.close();
    setTimeout(() => {
      if(document.querySelector('cart-drawer.active, cart-notification.active')){
        document.body.classList.add('overflow-hidden');
      }
    }, 600);
  }
  onSizeChartOpen(){
    this.modal?.classList.add('sticky-cart-popup--behind');
    document.body.addEventListener('modalClosed', () => {
      this.modal?.classList.remove('sticky-cart-popup--behind');
    }, { once: true });
  }
  // Drag to dismiss, bound to the grabber only so it never fights the sheet's
  // own scrolling.
  initDrag(){
    const handle = this.querySelector('.sticky-cart-popup__handle');
    const content = this.closest('.popup-modal__content');
    if(!handle || !content) return;

    let startY = null;
    let delta = 0;

    handle.addEventListener('touchstart', (event) => {
      startY = event.touches[0].clientY;
      delta = 0;
      content.style.transition = 'none';
    }, { passive: true });

    handle.addEventListener('touchmove', (event) => {
      if(startY === null) return;
      delta = Math.max(0, event.touches[0].clientY - startY);
      content.style.transform = `translateY(${delta}px)`;
    }, { passive: true });

    const onEnd = () => {
      if(startY === null) return;
      content.style.transition = '';
      content.style.transform = '';
      if(delta > 80) this.modal?.close();
      startY = null;
    };
    handle.addEventListener('touchend', onEnd);
    handle.addEventListener('touchcancel', onEnd);
  }
}
customElements.define('sticky-cart-popup', StickyCartPopup);
